import React from 'react';
import { Form, Input, InputNumber, Select, Switch, Tooltip } from 'antd';
import { InfoCircleOutlined, LockOutlined } from '@ant-design/icons';

/**
 * Tiny JSON-Schema → AntD Form renderer.
 *
 * Why not RJSF? Our connector schemas are intentionally flat (a single
 * object with primitive properties), so a 100-line custom renderer keeps
 * the bundle small and lets us wire AntD form semantics + tenant theming
 * without fighting RJSF's CSS. Supports:
 *   - string / integer / number / boolean
 *   - enum (→ Select)
 *   - format: "password" or `x-secret: true` (→ Input.Password)
 *   - format: "email", "uri", "url"
 *   - description (→ helper text + tooltip)
 *   - required (top-level only)
 *   - default values
 *
 * Anything more exotic (oneOf/anyOf/nested objects/arrays of objects)
 * falls back to a JSON-text textarea so the user can still proceed.
 */
// Extract defaults for fields the caller didn't override.
export function computeDefaults(props, initialValues) {
  const defaults = {};
  for (const [key, sub] of Object.entries(props)) {
    if (initialValues && key in initialValues) {continue;}
    if (sub && 'default' in sub) {defaults[key] = sub.default;}
  }
  return defaults;
}

export default function JsonSchemaForm({ schema, form, initialValues }) {
  if (!schema || typeof schema !== 'object') {
    return null;
  }
  const props = schema.properties || {};
  const required = new Set(schema.required || []);
  const defaults = computeDefaults(props, initialValues);
  const merged = { ...defaults, ...(initialValues || {}) };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={merged}
      requiredMark
    >
      {Object.entries(props).map(([key, sub]) =>
        renderField(key, sub, required.has(key))
      )}
    </Form>
  );
}

// ── enums → Select ──────────────────────────────────────────────────
function renderEnumField(name, sub, label, help, baseRules, isRequired) {
  return (
    <Form.Item
      key={name}
      name={name}
      label={renderLabel(label, help)}
      rules={baseRules}
      tooltip={help && { title: help, icon: <InfoCircleOutlined /> }}
    >
      <Select
        allowClear={!isRequired}
        options={sub.enum.map((v) => ({ value: v, label: String(v) }))}
      />
    </Form.Item>
  );
}

// ── booleans → Switch ───────────────────────────────────────────────
function renderBooleanField(name, _sub, label, help) {
  return (
    <Form.Item
      key={name}
      name={name}
      label={renderLabel(label, help)}
      valuePropName="checked"
      tooltip={help && { title: help, icon: <InfoCircleOutlined /> }}
    >
      <Switch />
    </Form.Item>
  );
}

// ── numbers ───────────────────────────────────────────────────────
function renderNumberField(name, sub, label, help, baseRules) {
  const rules = [...baseRules, { type: 'number' }];
  return (
    <Form.Item
      key={name}
      name={name}
      label={renderLabel(label, help)}
      rules={rules}
      tooltip={help && { title: help, icon: <InfoCircleOutlined /> }}
    >
      <InputNumber
        min={sub.minimum}
        max={sub.maximum}
        step={sub.type === 'integer' ? 1 : undefined}
        style={{ width: '100%' }}
      />
    </Form.Item>
  );
}

// ── strings ───────────────────────────────────────────────────────
function buildStringRules(sub, baseRules) {
  const rules = [...baseRules];
  const isUrl = sub.format === 'uri' || sub.format === 'url';
  const isEmail = sub.format === 'email';
  if (isUrl) {rules.push({ type: 'url', message: 'Must be a valid URL' });}
  if (isEmail) {rules.push({ type: 'email', message: 'Must be a valid email' });}
  if (sub.minLength) {rules.push({ min: sub.minLength });}
  if (sub.maxLength) {rules.push({ max: sub.maxLength });}
  if (sub.pattern) {
    try {
      const rx = new RegExp(sub.pattern);
      rules.push({ pattern: rx, message: `Must match ${sub.pattern}` });
    } catch { /* ignore invalid pattern */ }
  }
  return rules;
}

// maxLength above which a string field renders as a textarea.
const LONG_TEXT_MIN_LENGTH = 200;

function pickStringWidget(sub) {
  const isSecret = sub['x-secret'] === true || sub.format === 'password';
  const isLong = (sub.maxLength || 0) > LONG_TEXT_MIN_LENGTH || sub.format === 'textarea';
  if (isSecret) {
    return <Input.Password autoComplete="new-password" placeholder={sub.examples?.[0]} prefix={<LockOutlined />} />;
  }
  if (isLong) {
    return <Input.TextArea rows={4} placeholder={sub.examples?.[0]} />;
  }
  return <Input placeholder={sub.examples?.[0]} />;
}

function renderStringField(name, sub, label, help, baseRules) {
  const rules = buildStringRules(sub, baseRules);
  const widget = pickStringWidget(sub);
  return (
    <Form.Item
      key={name}
      name={name}
      label={renderLabel(label, help)}
      rules={rules}
      tooltip={help && { title: help, icon: <InfoCircleOutlined /> }}
    >
      {widget}
    </Form.Item>
  );
}

// ── fallback: raw JSON text ─────────────────────────────────────────
function renderFallbackField(name, _sub, label, help, baseRules) {
  return (
    <Form.Item
      key={name}
      name={name}
      label={renderLabel(label, help)}
      tooltip="Advanced field — provide JSON value"
      rules={baseRules}
    >
      <Input.TextArea rows={3} />
    </Form.Item>
  );
}

export function resolveFieldKind(sub) {
  if (Array.isArray(sub.enum)) {return 'enum';}
  if (sub.type === 'boolean') {return 'boolean';}
  if (sub.type === 'integer' || sub.type === 'number') {return 'number';}
  if (!sub.type || sub.type === 'string') {return 'string';}
  return 'fallback';
}

const FIELD_RENDERERS = {
  enum: renderEnumField,
  boolean: renderBooleanField,
  number: renderNumberField,
  string: renderStringField,
  fallback: renderFallbackField,
};

function renderField(name, sub, isRequired) {
  if (!sub || typeof sub !== 'object') {return null;}
  const label = sub.title || humanize(name);
  const help = sub.description;
  const baseRules = isRequired ? [{ required: true, message: `${label} is required` }] : [];
  const kind = resolveFieldKind(sub);
  return FIELD_RENDERERS[kind](name, sub, label, help, baseRules, isRequired);
}

function renderLabel(label, help) {
  if (!help) {return label;}
  return (
    <span>
      {label}{' '}
      <Tooltip title={help}>
        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
      </Tooltip>
    </span>
  );
}

function humanize(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
